import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/** User theme choice. `system` defers to `prefers-color-scheme`. */
export type ThemePreference = 'light' | 'dark' | 'system';

interface ThemeContextValue {
  /** The stored preference (what the user picked). */
  preference: ThemePreference;
  /**
   * The theme actually applied right now: in the console, `system` resolved
   * against the OS; on the public site, always light.
   */
  resolved: 'light' | 'dark';
  setPreference: (preference: ThemePreference) => void;
}

/**
 * A rota atual e o aviso de cada navegação: o formato do data router do React
 * Router (`createBrowserRouter`). O provedor fica ACIMA do RouterProvider, onde
 * não há `useLocation`, então o App entrega o próprio roteador.
 */
export interface ThemeRouter {
  readonly state: { readonly location: { readonly pathname: string } };
  subscribe(listener: (state: { location: { pathname: string } }) => void): () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = 'habilita.theme';

/**
 * O papel (--bg) de cada tema para o cromo do navegador (`theme-color`). O claro
 * é o que o index.html declara; o escuro só vale no console em tema escuro.
 */
export const THEME_COLOR = { light: '#fcf8f3', dark: '#16141d' } as const;

/**
 * O tema é do console: `/app` e o que vem abaixo dele. O site público (landing,
 * perfis, planos, FAQ, páginas legais, login, assinatura) é sempre claro, seja
 * qual for a preferência ou o sistema operacional. public/theme-init.js repete
 * esta regra antes da primeira pintura.
 */
export function isConsolePath(pathname: string): boolean {
  return pathname === '/app' || pathname.startsWith('/app/');
}

function readPreference(): ThemePreference {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    // Storage unavailable; fall through to the default.
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-color-scheme: dark)').matches);
}

/** O `content` de uma meta do <head>, criada se faltar (o index.html traz as duas). */
function setMeta(name: string, content: string): void {
  let meta = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.append(meta);
  }
  meta.content = content;
}

/**
 * Applies the theme by setting `data-theme` on the document root, matching the
 * token swap in tokens.css — only in the console. There the attribute is the
 * preference itself (`system` included, which the media-query block resolves);
 * anywhere else it is removed, and the site paints the light tokens. The browser
 * chrome follows the applied theme (`color-scheme` and `theme-color` metas).
 * Persists the user's explicit choice.
 *
 * Every navigation re-evaluates where the user is: leaving the console for the
 * landing takes the theme off before that frame is painted. Without a router
 * (tests, an island outside the app), the URL at mount decides.
 */
export function ThemeProvider({ children, router }: { children: ReactNode; router?: ThemeRouter }): JSX.Element {
  const [preference, setPreferenceState] = useState<ThemePreference>(readPreference);
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);
  const [inConsole, setInConsole] = useState<boolean>(() =>
    isConsolePath(router?.state.location.pathname ?? window.location.pathname),
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (): void => setSystemDark(mql.matches);
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    if (!router) return;
    // Uma navegação entre o primeiro render e a assinatura não passa em branco.
    setInConsole(isConsolePath(router.state.location.pathname));
    return router.subscribe((state) => setInConsole(isConsolePath(state.location.pathname)));
  }, [router]);

  const resolved: 'light' | 'dark' =
    !inConsole ? 'light' : preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;

  // Antes da pintura: a rota nova nunca aparece um quadro no tema da anterior.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (inConsole) {
      root.setAttribute('data-theme', preference);
    } else {
      root.removeAttribute('data-theme');
    }
    setMeta('color-scheme', resolved);
    setMeta('theme-color', THEME_COLOR[resolved]);
  }, [inConsole, preference, resolved]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Best-effort persistence; the in-memory state still applies for the session.
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider.');
  }
  return ctx;
}
